import { computed, DestroyRef, HostAttributeToken, inject, Injector, Signal, signal } from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import { FormField } from '@angular/forms/signals';

/** The host's own `aria-describedby` followed by `extra`, or `null` when both are empty; call it in a field initializer. */
export function fieldDescribedBy(extra: Signal<string | null>): Signal<string | null> {
  const own = inject(new HostAttributeToken('aria-describedby'), { optional: true });
  return computed(() => [own, extra()].filter(Boolean).join(' ') || null);
}

/**
 * What a field needs from the form control it is bound to: whether to draw its error state.
 * The control is looked up lazily, since the field is its own value accessor and cannot inject it.
 */
export class MibFieldControl {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private control: NgControl | null = null;
  /** Set under `[formField]`, whose state is read through its signals instead of the control's events. */
  private readonly formField = signal<FormField<unknown> | null>(null);
  private readonly controlShowsError = signal(false);
  private readonly controlRequired = signal(false);
  private readonly controlErrorKeys = signal<string[]>([]);
  private readonly state = computed(() => this.formField()?.state());

  /** Invalid once the reader has touched or changed it, as a field shows its error. */
  readonly showsError = computed(() => {
    const state = this.state();
    return state ? state.invalid() && (state.touched() || state.dirty()) : this.controlShowsError();
  });

  /** Whether the control carries `Validators.required`, or the field a `required` rule. */
  readonly required = computed(() => this.state()?.required() ?? this.controlRequired());

  /** The keys of the errors the control holds. */
  readonly errorKeys = computed(() => {
    const state = this.state();
    return state ? state.errors().map((error) => error.kind) : this.controlErrorKeys();
  });

  connect(): void {
    const formField = this.injector.get(FormField, null, { self: true, optional: true });
    if (formField) return this.formField.set(formField);
    this.control = this.injector.get(NgControl, null, { self: true, optional: true });
    const events = this.control?.control?.events;
    if (!events) return;
    const sub = events.subscribe(() => this.refresh());
    this.destroyRef.onDestroy(() => sub.unsubscribe());
    this.refresh();
  }

  /** Rewrites the value of the control without marking it dirty; under `[formField]` the view is written again. */
  replaceValue(value: unknown): void {
    const state = this.state();
    if (state) state.value.set(value);
    else this.control?.control?.setValue(value, { emitModelToViewChange: false });
  }

  refresh(): void {
    const c = this.control;
    this.controlShowsError.set(!!c && !!c.invalid && !!(c.touched || c.dirty));
    this.controlRequired.set(!!c?.control?.hasValidator(Validators.required));
    this.controlErrorKeys.set(Object.keys(c?.errors ?? {}));
  }
}
